import React, { useEffect, useMemo } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { useLoginProviderList } from '../../api/state'
import { useRoleOrgOrganization, useRoleOrgSSO } from '../../role/hook/role-org'
import { OffcanvasClose } from '../../shared/component/offcanvas'
import { useModalActions } from '../../shared/component/modal-dialog'
import { BuildInfo } from '../../shared/component/build-info'
import { Link } from 'react-router-dom'
import { usePendingInvites } from '../../shared/sso-workspace/pending-invites'
import { canSwitchOrganization } from '../../shared/organization-switch'
import { THEME_TYPE_DARK, THEME_TYPE_LIGHT, THEME_TYPE_SYSTEM, usePageContext } from '../../context/page.context'
import { AccountMenuAccounts } from './account-menu-accounts'
import {
  IconOrganization,
  IconThemeLight,
  IconThemeDark,
  IconPassword,
  IconPurge,
  IconLink,
  IconShield,
  IconThemeSystem,
  IconAuthLogin,
  IconEmail,
  IconLoginDevice
} from '../../shared/icon/icon'

const SidebarItemNavLink = ({ Icon, title, to, target, badge, badgeLabel }) => {
  return (
    <OffcanvasClose target={target}>
      <li className="nav-item py-1 edxr-sidebar-right-item edxr-pointer">
        <Link className="nav-link text-decoration-none text-uppercase fw-light" to={to || ''}>
          <Icon size="lg" />
          <span className="mx-2">
            {title}
          </span>
          {badge > 0 &&
            <span className="badge rounded-pill text-bg-primary" aria-label={badgeLabel}>
              {badge}
            </span>}
        </Link>
      </li>
    </OffcanvasClose>
  )
}

const SidebarItemNavClick = ({ Icon, title, target, onClick, testID }) => {
  return (
    <OffcanvasClose target={target}>
      <li className="nav-item py-1 edxr-sidebar-right-item edxr-pointer" data-testid={testID}>
        <span className="nav-link text-decoration-none text-uppercase fw-light" onClick={onClick}>
          <Icon size="lg" />
          <span className="mx-2">
            {title}
          </span>
        </span>
      </li>
    </OffcanvasClose>
  )
}

export const SidebarRight = ({ target, signInDeviceTarget }) => {
  const authContext = useAuthContext()
  const pageContext = usePageContext()

  // "Sign in another device" is here only when the header has no room for
  // it (header-fit.js; owner 2026-10-19): the header passes the code modal's
  // id then, and nothing otherwise, so it is never in both places. The modal
  // opens once this panel has closed, so the two never hold the page at once.
  const signInDeviceModal = useModalActions(signInDeviceTarget)
  const handleSignInDevice = () => {
    const panel = target && document.getElementById(target)
    if (panel && panel.classList.contains('show')) {
      panel.addEventListener('hidden.bs.offcanvas', () => signInDeviceModal.show(), { once: true })
    } else {
      signInDeviceModal.show()
    }
  }

  const handleSystem = () => pageContext.theme.actions.setThemeSystem()
  const handleLight = () => pageContext.theme.actions.setThemeLight()
  const handleDark = () => pageContext.theme.actions.setThemeDark()

  const [loginProviderListActions, loginProviderListState] = useLoginProviderList()

  // The Authentication page's server endpoints all require ORG_SSO (not
  // USER): show it to exactly the people it will work for.
  const roleOrgSSO = useRoleOrgSSO()

  // The organization's own page (delete organization, plan Task 26): read by
  // its owners and admins -- a super admin only through their actual role
  // there (owner ruling 2026-10-07) -- and an impersonated session whose
  // person reads it (useRoleOrgOrganization).
  const roleOrgOrganization = useRoleOrgOrganization()

  useEffect(
    () => {
      if (authContext.state.jwtClaims.loginID) {
        loginProviderListActions.get(authContext.state.jwtClaims.loginID)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authContext.state.jwtClaims]
  )

  const showProviders = useMemo(() => loginProviderListState.items.length > 0, [loginProviderListState.items])

  // Pending invitations while signed in (owner feedback 2026-09-27): the
  // shared cached count (usePendingInvites) -- refreshed when stale on tab
  // focus and right after an answer on /auth/invitations, not on every
  // navigation (re-review 5 RM5-3). An impersonated session is given none.
  const pendingInvites = usePendingInvites()

  return (
    <div className="d-flex flex-column h-100">
      {/* The account part (owner 2026-11-02, "like Google's"): the account
          row, Sign out under it, and the well the chevron opens between them
          -- the other remembered accounts (Sign in in a new tab), Add another
          account, Remove an account (edit mode, where "Forget this account on
          this device" went) and Manage your EducationXR account
          (account-menu-accounts.js). Nothing about the account is down in
          the list below any more. */}
      <AccountMenuAccounts target={target} />

      <div className="flex-fill">
        <ul className="nav nav-pills flex-column mt-3 justify-content-between">
          {/* the same decision as the sidebar's organization card */}
          {canSwitchOrganization(authContext.state.jwtClaims) &&
            <SidebarItemNavLink Icon={IconOrganization} title="Change Organization" to="/auth/account" target={target} />}

          {signInDeviceTarget &&
            <SidebarItemNavClick
              Icon={IconLoginDevice}
              title="Sign in another device"
              target={target}
              onClick={handleSignInDevice}
              testID="account-menu-sign-in-device"
            />}

          <SidebarItemNavLink
            Icon={IconEmail}
            title="Invitations"
            to="/auth/invitations"
            target={target}
            badge={pendingInvites.count}
            badgeLabel={`${pendingInvites.count} pending`}
          />

          {showProviders && <SidebarItemNavLink title="Sign-in connections" Icon={IconLink} to="/org/login-provider" target={target} />}
          {roleOrgSSO.accessRead && <SidebarItemNavLink title="Authentication" Icon={IconShield} to="/org/sso" target={target} />}
          {roleOrgOrganization.accessRead && <SidebarItemNavLink title="Organization" Icon={IconOrganization} to="/org/organization" target={target} />}
          <SidebarItemNavLink title="Update Password" Icon={IconPassword} to="/org/password-update" target={target} />
          <SidebarItemNavLink title="Login Sessions" Icon={IconAuthLogin} to="/org/login-session" target={target} />

          {/* the destructive item sits apart, last, so a click aimed at another entry can't land on it */}
          <li className="border-top my-2" role="separator" data-testid="account-menu-danger-separator" />
          <SidebarItemNavLink title="Delete account" Icon={IconPurge} to="/org/delete-account" target={target} />
        </ul>
      </div>

      <div className="border-top">
        <div className="m-2 mb-1 fw-bold">Appearance</div>
        <div className="d-flex ">
          {pageContext.theme.hasSystemTheme &&
            <Button Icon={IconThemeSystem} text="System" selected={pageContext.theme.type === THEME_TYPE_SYSTEM} onClick={handleSystem} />}
          <Button Icon={IconThemeLight} text="Light" selected={pageContext.theme.type === THEME_TYPE_LIGHT} onClick={handleLight} />
          <Button Icon={IconThemeDark} text="Dark" selected={pageContext.theme.type === THEME_TYPE_DARK} onClick={handleDark} />
        </div>
      </div>

      {/* Footer, below Appearance: which build is this tab running. The quietest
          thing on the panel, so it sits last and takes no emphasis from the
          controls above it. */}
      <BuildInfo />
    </div>
  )
}

const Button = ({ Icon, text, selected, onClick }) => {
  return (
    <div className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-secondary'} flex-fill m-1`} onClick={onClick}>
      <div>
        <Icon />
      </div>
      <div>
        {text}
      </div>
    </div>
  )
}
